import { parse } from "date-fns";
import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, usePermanentRealtimeDealers } from "components";
import { createFormHelper } from "lib/Form";
import { DMSIDS } from "models";
import "modules/DMSv3/Settings/components/AddEditDealerModal/AddEditDealerModal.scss";
import { AddEditDealerForm } from "modules/DMSv3/Settings/components/AddEditDealerModal/components";
import { useCreateDmsDealerMutation, useUpdateDmsDealerMutation } from "modules/DMSv3/Settings/components/AddEditDealerModal/hooks";

import type { DealerDMS } from "models";
import type {
  AutolineConfig,
  CarITConfig,
  EvaConfig,
  ICarConfig,
  IncadeaConfig,
  KeyloopConfig,
  NextlaneConfig,
  VinaclesConfig,
  WincarConfig
} from "modules/DMSv3/Settings/components/AddEditDealerModal/components";
import type { CreateDmsDealerPayload } from "modules/DMSv3/Settings/components/AddEditDealerModal/hooks";

type PickedDealerDMS = Pick<DealerDMS, "dsn" | "is_interval_full_run_enabled" | "is_cron" | "import_date_created" | "is_custom_query_only">;

export type AddEditDealerFormPayload = PickedDealerDMS & {
  dealer_id?: number;
  time_to_sleep: number | null;
  duration_threshold: number | null;
  date_range: number | null;
  delay: number | null;
  start_time: Date | string;
  end_time: Date | string;
  full_run_hour_interval: number | "";
  dms_id?: DMSIDS;
  icar_config?: ICarConfig;
  incadea_config?: IncadeaConfig;
  keyloop_config?: KeyloopConfig;
  autoline_config?: AutolineConfig;
  eva_config?: EvaConfig;
  wincar_config?: WincarConfig;
  carit_config?: CarITConfig;
  nextlane_config?: NextlaneConfig;
  vinacles_config?: VinaclesConfig;
};

type AddEditDealerModalProps = {
  isOpen: boolean;
  selectedDealer: DealerDMS | undefined;
  onClose: () => void;
};

const isKeyloopLocation = (dmsId?: DMSIDS) =>
  dmsId && [DMSIDS.KEYLOOP_MENUS, DMSIDS.KEYLOOP_JOBS, DMSIDS.KEYLOOP_MENUS_STAGING, DMSIDS.KEYLOOP_JOBS_STAGING].includes(dmsId);

const FormHelper = createFormHelper<AddEditDealerFormPayload>();

export const AddEditDealerModal = ({ isOpen, selectedDealer, onClose }: AddEditDealerModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);
  const [maxCheckInDaysPrior, setMaxCheckInDaysPrior] = useState<number | null>(null);

  const { dealers } = usePermanentRealtimeDealers();
  const { mutateAsync: createDmsDealer, isPending: isCreateDmsDealerPending } = useCreateDmsDealerMutation();
  const { mutateAsync: updateDmsDealer, isPending: isUpdateDmsDealerPending } = useUpdateDmsDealerMutation();

  const handleClose = () => {
    onClose();
    setHasErrors(false);
    setMaxCheckInDaysPrior(null);
  };

  const isFormInvalid = (values: AddEditDealerFormPayload) => {
    let hasErrors =
      !values.dealer_id || !values.duration_threshold || !values.start_time || !values.end_time || !values.time_to_sleep || !values.date_range || !values.dms_id;
    if (isKeyloopLocation(values.dms_id) && !values.keyloop_config?.contract_code.trim()) hasErrors = true;

    const dealer = dealers.find(dealer => dealer.id === values.dealer_id);
    const maxDaysPrior =
      dealer?.locations?.reduce<number | null>(
        (max, location) =>
          location.online_check_in_enabled && location.dms_id === values.dms_id && (max === null || location.check_in_days_prior > max)
            ? location.check_in_days_prior
            : max,
        null
      ) ?? null;
    const isDateRangeTooLow = maxDaysPrior !== null && values.date_range !== null && values.date_range <= maxDaysPrior;
    if (isDateRangeTooLow) hasErrors = true;

    setMaxCheckInDaysPrior(isDateRangeTooLow ? maxDaysPrior : null);
    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: AddEditDealerFormPayload) => {
    if (isFormInvalid(values)) return;

    if (!values.dealer_id || !values.dms_id) return;
    const payload: CreateDmsDealerPayload = { ...values, dealer_id: values.dealer_id, dms_id: values.dms_id };

    if (selectedDealer) await updateDmsDealer(payload);
    else await createDmsDealer(payload);
    handleClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} closeOnDimmerClick={false} size="large">
      <div className="AddEditDealerModal">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={
            selectedDealer
              ? {
                  ...selectedDealer,
                  time_to_sleep: selectedDealer.time_to_sleep ? Number(selectedDealer.time_to_sleep) : null,
                  duration_threshold: selectedDealer.duration_threshold ? Number(selectedDealer.duration_threshold) : null,
                  delay: selectedDealer.delay ? Number(selectedDealer.delay) : null,
                  start_time: parse(selectedDealer.start_time, DATE_FORMATS_FNS.hoursMinutesSeconds, new Date()),
                  end_time: parse(selectedDealer.end_time, DATE_FORMATS_FNS.hoursMinutesSeconds, new Date())
                }
              : {
                  dealer_id: undefined,
                  dsn: "",
                  time_to_sleep: null,
                  date_range: null,
                  start_time: "",
                  end_time: "",
                  duration_threshold: null,
                  is_interval_full_run_enabled: false,
                  full_run_hour_interval: "",
                  delay: null,
                  is_cron: false,
                  import_date_created: false,
                  is_custom_query_only: false,
                  dms_id: undefined
                }
          }
        >
          <AddEditDealerForm
            hasErrors={hasErrors}
            maxCheckInDaysPrior={maxCheckInDaysPrior}
            isPending={isCreateDmsDealerPending || isUpdateDmsDealerPending}
            selectedDealer={selectedDealer}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </FormHelper.Form>
      </div>
    </Modal>
  );
};
